<template>
  <div>
    <slot v-if="disabled"></slot>
    <template v-else-if="reverseScroll">
      <infinite-loading direction="top" @infinite="infiniteLoadHandler" :identifier="identifier">
        <!-- overwriting vue-infinite-loading default messages -->
        <template v-slot:spinner>
          <slot name="spinner">
            <div>Loading...</div>
          </slot>
        </template>
        <template v-slot:no-more>
          <slot name="no-more">
            <span class="sr-only">You've reached the end of the list</span>
          </slot>
        </template>
        <template v-slot:no-results>
          <slot name="no-results">
            <span class="sr-only">No results found.</span>
          </slot>
        </template>
        <template v-slot:error="{ trigger }">
          Loading failed. Error has occurred.<br><button @click="trigger" class="btn-try-infinite">Retry</button>
        </template>
      </infinite-loading>
      <slot></slot>
    </template>
    <div v-else v-infinite-scroll="loadMore" infinite-scroll-immediate-check infinite-scroll-disabled="disableInfiniteScroll" infinite-scroll-distance="10">
      <slot></slot>

      <div class="d-flex justify-content-center infinite-scroll-end">
        <div v-if="busy">
          <div class="spinner-border" role="status">
            <span class="sr-only" >Loading...</span>
          </div>
          <span v-if="hasText">Loading {{text}}...</span>
          <span v-else>Loading...</span>
        </div>
        <div v-else-if="fullyLoaded && empty">{{emptyMessage}}</div>
        <div v-else-if="fullyLoaded">{{fullyLoadedMessage}}</div>
        <!-- else, intentionally empty -->
      </div>
    </div>
  </div>

</template>

<script lang="ts">
  import Vue from "vue";
  import infiniteScroll from "vue-infinite-scroll";
  import InfiniteLoading from 'vue-infinite-loading';
  import * as _ from "lodash";
  import lockstepDebounce from "~/lockstepDebounce";

  // Including 2 infinite scroll packages as vue-infinite-scroll does not allow loading from top of scroll area. Research whether vue-infinite-loading is maintained well enough to migrate to.
  // vue-infinite-scroll has had a PR open to allow top loading for about a year, maybe they will add this functionality?

  export default Vue.extend({
    components: {
      InfiniteLoading,
    },
    props: {
      busy: Boolean,
      fullyLoaded: Boolean,
      disabled: {
        type: Boolean,
        default: false
      },
      reverseScroll: {
        type: Boolean,
        default: false,
      },
      identifier: {
        // can be any type. see https://peachscript.github.io/vue-infinite-loading/api/#identifier
        default: Date.now(),
      },
      text: {
        type: String,
        default: ""
      },
      fullyLoadedMessage: {
        type: String,
        default: "You've reached the end of the list"
      },
      empty: Boolean,
      emptyMessage: {
        type: String,
        default: "You've reached the end of the list"
      },
    },
    directives: {infiniteScroll},
    computed: {
      disableInfiniteScroll() {
        return this.busy || this.fullyLoaded;
      },
      debouncedLoadMore(): Function {
        return lockstepDebounce(() => this.$emit('loadMore'), window.AppConfig.scrollLoaderTimeout());
      },
      debouncedInfiniteLoadHandler(): Function {
        return lockstepDebounce(($state) => this.$emit('loadMore', $state), window.AppConfig.scrollLoaderTimeout());
      },
      hasText() {
        return this.text.length > 0
      }
    },
    methods: {
      loadMore() {
        this.debouncedLoadMore();
      },
      // keeping logic for 2 packages separate for now.
      infiniteLoadHandler($state) {
        this.debouncedInfiniteLoadHandler($state);
      },
    },
  });
</script>
